import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Loader2, CreditCard, CalendarClock, ShieldCheck, XCircle, ExternalLink } from "lucide-react";
import { toast } from "sonner";

const STATUS_COLORS = {
  active:    { bg: "bg-emerald-50",  text: "text-emerald-800",  ring: "ring-emerald-200",  label: "Active" },
  approval_pending: { bg: "bg-amber-50", text: "text-amber-800", ring: "ring-amber-200", label: "Awaiting approval" },
  approved:  { bg: "bg-amber-50",   text: "text-amber-800",   ring: "ring-amber-200",   label: "Approved — finalising" },
  suspended: { bg: "bg-orange-50",  text: "text-orange-800",  ring: "ring-orange-200",  label: "Suspended" },
  cancelled: { bg: "bg-stone-100",  text: "text-stone-700",   ring: "ring-stone-200",   label: "Cancelled" },
  expired:   { bg: "bg-stone-100",  text: "text-stone-700",   ring: "ring-stone-200",   label: "Expired" },
};

/** "My ChildCheck Subscription" panel — appears at the top of /dashboard/billing.
 * Reads the owner's subscription from /api/me/subscription, supports cancel.
 */
export default function MySubscriptionPanel() {
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/me/subscription");
      setData(r.data);
    } catch (err) {
      // Silent for owners with no subscription on file (e.g. seed/superadmin-created accounts).
      setData({ subscription: null });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const cancel = async () => {
    if (!window.confirm("Cancel your ChildCheck subscription? You'll keep access until the end of the current billing period.")) return;
    setBusy(true);
    try {
      await api.post("/me/subscription/cancel");
      toast.success("Subscription cancelled. PayPal will stop future charges.");
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div data-testid="my-subscription-loading" className="rounded-2xl border border-stone-200 bg-white p-6 flex items-center gap-3 text-stone-500">
        <Loader2 className="w-4 h-4 animate-spin" /> Checking subscription…
      </div>
    );
  }

  const sub = data?.subscription;

  // ---- Deep-link helpers ------------------------------------------------
  // PayPal's per-subscription management URL. In practice this deep link
  // opens the customer's PayPal login and lands them directly on the
  // Automatic Payments detail page for THIS subscription — where they can
  // update payment method, view invoices, or cancel.
  const paypalManageUrl = (subscriptionId) =>
    subscriptionId
      ? `https://www.paypal.com/myaccount/autopay/connect/${encodeURIComponent(subscriptionId)}`
      : "https://www.paypal.com/myaccount/autopay/";

  // No subscription on file
  if (!sub || !sub.subscription_id) {
    const status = (sub?.status || "").toLowerCase();
    const trialing = status === "trialing";
    // iter-74: Superadmin-managed centers have `subscription.status = "active"`
    // but no `subscription_id` (they were provisioned manually — the operator
    // reconciles billing off-platform). Show a distinct, non-scary card that
    // makes clear the account IS active and points to superadmin support.
    const managedBySuperadmin = status === "active";
    if (managedBySuperadmin) {
      return (
        <div data-testid="my-subscription-managed" className="rounded-2xl border border-emerald-200 bg-emerald-50/60 p-6">
          <div className="flex items-start gap-3">
            <ShieldCheck className="w-5 h-5 text-emerald-700 mt-0.5" />
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <div className="font-display text-lg font-medium text-stone-900">
                  Managed subscription — Active
                </div>
                <span className="text-[10px] uppercase tracking-widest bg-emerald-700 text-white px-2 py-0.5 rounded-full font-semibold">
                  Active
                </span>
              </div>
              <p className="mt-1 text-sm text-stone-600">
                Your center is provisioned directly by the ChildCheck Ohio team — billing runs off-platform (invoice, ACH, or check).
                For receipts, plan changes, or cancellation, email <a href="mailto:support@childcheckoh.com" className="text-emerald-700 font-semibold">support@childcheckoh.com</a> and we&apos;ll take care of it.
              </p>
              {sub?.plan_id && (
                <div className="mt-2 text-xs text-stone-500">
                  Plan on file: <b className="font-semibold text-stone-700">{(sub.plan_name || sub.plan_id || "").toString()}</b>
                </div>
              )}
              <div className="mt-3">
                <Button
                  asChild
                  data-testid="my-subscription-contact-support"
                  variant="outline"
                  className="rounded-xl h-9 text-sm border-emerald-300 text-emerald-800 hover:bg-emerald-100"
                >
                  <a href="mailto:support@childcheckoh.com?subject=Subscription%20request%20%E2%80%93%20ChildCheckOh">
                    <ExternalLink className="w-3.5 h-3.5 mr-1.5" /> Email support
                  </a>
                </Button>
              </div>
            </div>
          </div>
        </div>
      );
    }
    return (
      <div data-testid="my-subscription-none" className="rounded-2xl border border-amber-200 bg-amber-50/60 p-6">
        <div className="flex items-start gap-3">
          <CreditCard className="w-5 h-5 text-amber-700 mt-0.5" />
          <div className="flex-1">
            <div className="font-display text-lg font-medium text-stone-900">
              {trialing ? "You're on the free trial" : "No active subscription on file"}
            </div>
            <p className="mt-1 text-sm text-stone-600">
              {trialing
                ? "Pick a plan when you're ready — your center stays active in the meantime."
                : "If you signed up via PayPal, the link will appear here as soon as your first payment clears."}
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              <Button
                asChild
                data-testid="my-subscription-choose-plan"
                className="rounded-xl h-9 text-sm bg-emerald-700 hover:bg-emerald-800 text-white"
              >
                <a href="/pricing">Choose a plan</a>
              </Button>
              <Button
                asChild
                data-testid="my-subscription-open-paypal-autopay"
                variant="outline"
                className="rounded-xl h-9 text-sm"
              >
                <a href="https://www.paypal.com/myaccount/autopay/" target="_blank" rel="noreferrer">
                  <ExternalLink className="w-3.5 h-3.5 mr-1.5" /> PayPal Automatic Payments
                </a>
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const statusKey = (sub.status || "").toLowerCase();
  const statusStyle = STATUS_COLORS[statusKey] || { bg: "bg-stone-100", text: "text-stone-700", ring: "ring-stone-200", label: sub.status || "Unknown" };
  const isCancellable = statusKey === "active" || statusKey === "suspended" || statusKey === "approval_pending" || statusKey === "approved";
  const nextBilling = sub.next_billing_time ? new Date(sub.next_billing_time) : null;

  return (
    <div data-testid="my-subscription-panel" className="rounded-2xl border border-stone-200 bg-white p-6 shadow-soft">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-emerald-700">My ChildCheck subscription</div>
          <div className="mt-2 flex items-center gap-3 flex-wrap">
            <h2 className="font-display text-2xl font-medium text-stone-900">
              {sub.plan_name || sub.plan_id || "ChildCheck"} <span className="text-stone-500 text-base">· ${Number(sub.amount || 0).toFixed(0)}/mo</span>
            </h2>
            <span data-testid="my-subscription-status" className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ring-1 ${statusStyle.bg} ${statusStyle.text} ${statusStyle.ring}`}>
              {statusStyle.label}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            asChild
            data-testid="my-subscription-manage-paypal"
            className="rounded-xl h-9 text-sm bg-[#0070ba] hover:bg-[#005a94] text-white shadow-sm"
          >
            <a href={paypalManageUrl(sub.subscription_id)} target="_blank" rel="noreferrer">
              <ExternalLink className="w-3.5 h-3.5 mr-1.5" /> Manage on PayPal
            </a>
          </Button>
          <Button
            data-testid="my-subscription-refresh"
            variant="outline"
            onClick={load}
            disabled={busy}
            className="rounded-xl h-9 text-sm"
          >
            <ShieldCheck className="w-3.5 h-3.5 mr-1.5" /> Refresh
          </Button>
          {isCancellable && (
            <Button
              data-testid="my-subscription-cancel"
              variant="outline"
              onClick={cancel}
              disabled={busy}
              className="rounded-xl h-9 text-sm border-red-200 text-red-700 hover:bg-red-50"
            >
              {busy ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <XCircle className="w-3.5 h-3.5 mr-1.5" />}
              Cancel subscription
            </Button>
          )}
        </div>
      </div>

      <div className="mt-2 text-[11px] text-stone-500 font-mono" data-testid="my-subscription-id">
        Subscription ID: {sub.subscription_id}
      </div>

      <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
        <Stat
          icon={CalendarClock}
          label="Next billing date"
          value={nextBilling ? nextBilling.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : "—"}
        />
        <Stat
          icon={CreditCard}
          label="Last payment"
          value={sub.last_payment_amount != null ? `$${Number(sub.last_payment_amount).toFixed(2)}` : "—"}
        />
        <Stat
          icon={ShieldCheck}
          label="Billed via"
          value={(sub.provider || "paypal").toUpperCase()}
          sub={sub.payer_email || ""}
        />
      </div>

      <div className="mt-4 text-xs text-stone-500 flex items-center gap-1.5">
        <ExternalLink className="w-3 h-3" />
        Need a receipt or to change payment method? <a href={paypalManageUrl(sub.subscription_id)} target="_blank" rel="noreferrer" data-testid="my-subscription-footer-link" className="text-emerald-700 font-semibold ml-1">Open this subscription on PayPal →</a>
      </div>
    </div>
  );
}

const Stat = ({ icon: Icon, label, value, sub }) => (
  <div className="rounded-xl bg-stone-50 border border-stone-200 px-4 py-3">
    <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.15em] font-semibold text-stone-500">
      <Icon className="w-3.5 h-3.5" /> {label}
    </div>
    <div className="mt-1 font-display text-lg text-stone-900">{value}</div>
    {sub && <div className="text-xs text-stone-500 truncate">{sub}</div>}
  </div>
);
